import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import AudioPopup from '../components/audioPopup/AudioPopup';
import {
	closeSharedAudioContext,
	createMediaRecorder,
	createRecordingPipeline,
	getAudioPlatform,
	getRecorderConfig,
	requestMicrophoneStream,
	resolveMicrophoneErrorCode,
	stopMediaRecorderSafely,
	stopMediaStream,
} from '../functions/audioPlatform';

const defaultAudioRecordingContext = {
	isRecording: false,
	handleStartRecording: async () => false,
	handleStopRecording: async () => null,
	acquireCallStream: async () => false,
	releaseCallStream: async () => {},
	getMonitorStream: () => null,
};

export const AudioRecordingContext = createContext(defaultAudioRecordingContext);

export const useAudioRecording = () => {
	const context = useContext(AudioRecordingContext);

	if (!context) {
		throw new Error('useAudioRecording must be used within AudioRecordingContextProvider');
	}

	return context;
};

const MIC_READY_DELAY_MS = 320;

const AudioRecordingContextProvider = ({ children }) => {
	const platformRef = useRef(getAudioPlatform());
	const [isRecording, setIsRecording] = useState(false);
	const [waitingForProcess, setWaitingForProcess] = useState(false);
	const [showPopupMicNotFound, setShowPopupMicNotFound] = useState(false);
	const [micErrorReason, setMicErrorReason] = useState(null);

	const chunksRef = useRef([]);
	const rawStreamRef = useRef(null);
	const recordStreamRef = useRef(null);
	const monitorStreamRef = useRef(null);
	const callStreamRef = useRef(null);
	const mediaRecorderRef = useRef(null);
	const readyTimerRef = useRef(null);

	const clearReadyTimer = useCallback(() => {
		if (readyTimerRef.current) {
			window.clearTimeout(readyTimerRef.current);
			readyTimerRef.current = null;
		}
	}, []);

	const showMicrophoneError = useCallback((error) => {
		setMicErrorReason(resolveMicrophoneErrorCode(error));
		setShowPopupMicNotFound(true);
	}, []);

	const stopActiveMediaRecorder = useCallback(async () => {
		const recorder = mediaRecorderRef.current;

		if (!recorder || recorder.state === 'inactive') {
			mediaRecorderRef.current = null;
			return;
		}

		await stopMediaRecorderSafely(recorder, { current: [] });
		mediaRecorderRef.current = null;
	}, []);

	const closeAudioResources = useCallback(async () => {
		clearReadyTimer();
		await stopActiveMediaRecorder();

		stopMediaStream(mediaRecorderRef.current?.stream);
		stopMediaStream(recordStreamRef.current);
		stopMediaStream(monitorStreamRef.current);
		stopMediaStream(callStreamRef.current);
		stopMediaStream(rawStreamRef.current);

		rawStreamRef.current = null;
		recordStreamRef.current = null;
		monitorStreamRef.current = null;
		callStreamRef.current = null;
		mediaRecorderRef.current = null;
		chunksRef.current = [];
		await closeSharedAudioContext();
	}, [clearReadyTimer, stopActiveMediaRecorder]);

	const openMicrophonePipeline = useCallback(async () => {
		if (
			rawStreamRef.current
			?.getTracks?.()
			.some((track) => track.readyState === 'live')
		) {
			return {
				rawStream: rawStreamRef.current,
				recordStream: recordStreamRef.current || rawStreamRef.current,
				monitorStream: monitorStreamRef.current || rawStreamRef.current,
			};
		}

		const rawStream = await requestMicrophoneStream({
			platform: platformRef.current,
		});
		const pipeline = await createRecordingPipeline(
			rawStream,
			platformRef.current,
		);

		rawStreamRef.current = rawStream;
		recordStreamRef.current = pipeline.recordStream;
		monitorStreamRef.current = pipeline.monitorStream;

		return {
			rawStream,
			recordStream: pipeline.recordStream,
			monitorStream: pipeline.monitorStream,
		};
	}, []);

	const acquireCallStream = useCallback(async () => {
		try {
			const { recordStream } = await openMicrophonePipeline();
			callStreamRef.current = recordStream;
			return true;
		} catch (error) {
			console.error('Mic access error:', error);
			showMicrophoneError(error);
			await closeAudioResources();
			return false;
		}
	}, [closeAudioResources, openMicrophonePipeline, showMicrophoneError]);

	const handleStartRecording = useCallback(async (options = {}) => {
		const { keepStream = false } = options;
		clearReadyTimer();
		chunksRef.current = [];

		let recordStream = keepStream ? callStreamRef.current : null;

		if (!recordStream) {
			try {
				const pipeline = await openMicrophonePipeline();
				recordStream = pipeline.recordStream;

				if (keepStream) {
					callStreamRef.current = recordStream;
				}
			} catch (error) {
				console.error('Mic access error:', error);
				showMicrophoneError(error);
				setIsRecording(false);
				if (!keepStream) {
					await closeAudioResources();
				}
				return false;
			}
		}

		if (typeof MediaRecorder === 'undefined') {
			showMicrophoneError({ code: 'MICROPHONE_UNSUPPORTED' });
			setIsRecording(false);
			return false;
		}

		const recorder = createMediaRecorder(recordStream, platformRef.current);
		const { timeslice } = getRecorderConfig(platformRef.current);

		recorder.ondataavailable = (event) => {
			if (event.data && event.data.size > 0) {
				chunksRef.current.push(event.data);
			}
		};

		recorder.onerror = (event) => {
			console.error('MediaRecorder error:', event.error);
		};

		try {
			recorder.start(timeslice);
		} catch (error) {
			console.error('MediaRecorder start error:', error);
			if (!keepStream) {
				await closeAudioResources();
			}
			setIsRecording(false);
			return false;
		}

		mediaRecorderRef.current = recorder;
		setWaitingForProcess(true);

		readyTimerRef.current = window.setTimeout(() => {
			setWaitingForProcess(false);
			setIsRecording(true);
		}, MIC_READY_DELAY_MS);

		return true;
	}, [
		clearReadyTimer,
		closeAudioResources,
		openMicrophonePipeline,
		showMicrophoneError,
	]);

	const handleStopRecording = useCallback(async (options = {}) => {
		const { keepStream = false } = options;

		clearReadyTimer();
		setWaitingForProcess(false);
		setIsRecording(false);

		const recorder = mediaRecorderRef.current;
		mediaRecorderRef.current = null;

		const blob = await stopMediaRecorderSafely(recorder, chunksRef);
		chunksRef.current = [];

		if (!keepStream) {
			await closeAudioResources();
		}

		return blob;
	}, [clearReadyTimer, closeAudioResources]);

	const releaseCallStream = useCallback(async () => {
		clearReadyTimer();
		setWaitingForProcess(false);
		setIsRecording(false);
		chunksRef.current = [];
		await closeAudioResources();
	}, [clearReadyTimer, closeAudioResources]);

	const getMonitorStream = useCallback(
		() =>
			monitorStreamRef.current
			|| rawStreamRef.current
			|| callStreamRef.current
			|| mediaRecorderRef.current?.stream
			|| null,
		[],
	);

	useEffect(
		() => () => {
			void closeAudioResources();
		},
		[closeAudioResources],
	);

	return (
		<AudioRecordingContext.Provider
			value={{
				isRecording,
				handleStartRecording,
				handleStopRecording,
				acquireCallStream,
				releaseCallStream,
				getMonitorStream,
			}}
		>
			{showPopupMicNotFound && (
				<AudioPopup
					errorReason={micErrorReason}
					setClose={() => {
						setShowPopupMicNotFound(false);
						setMicErrorReason(null);
					}}
				/>
			)}
			{children}
		</AudioRecordingContext.Provider>
	);
};

export default AudioRecordingContextProvider;
